/* The redesigned console's dialogs. Every one runs on the same hook as
   today's modal (hooks/useAdminForms, hooks/useAdminConsole). */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog } from '../ui';
import {
    useAddUserForm, useAttachSource, useOnboardWizard, useOrgEditForm, ONBOARD_STEPS,
} from '../../hooks/useAdminForms';
import {
    COPY, deleteBlockerText, deleteBlockers, EDIT_TYPE_LABEL, findNode, resultCopy, TYPE_LABEL,
} from '../../utils/adminConsole';
import { accessSummary } from '../../utils/adminAccess';
import { DEFAULT_DATABASE } from '../AdminConsole/DataSourceFields';
import useConfirmGuard from '../../hooks/useConfirmGuard';
import {
    AccessChip, CheckPending, CheckResult, Note, PrefixInput, SourceChip, SourceFields,
} from './bits';
import { MailIcon, PlusIcon, SearchIcon } from './icons';

const indent = (depth) => '  '.repeat(depth);

// ── Add user (P-ADM-085..101) ──────────────────────────────────────────
export function AddUserDialog({ orgs, me, tenants, onClose, onAdded }) {
    const f = useAddUserForm({ orgs, me, onAdded, onClose });
    const searchRef = useRef(null);
    const roleRef = useRef(null);
    useEffect(() => {
        if (f.picked) roleRef.current?.focus();
        else searchRef.current?.focus();
    }, [f.picked]);

    const isSuper = Boolean(me?.is_super_admin);
    const preview = isSuper && f.pickedOrg
        ? accessSummary({ org_id: f.orgId, role: f.role, status: 'active', apps: { pricepath: f.pricepath } }, orgs, tenants)
        : null;

    const footer = f.picked ? (
        <>
            <Button onClick={onClose} disabled={f.saving}>Cancel</Button>
            <Button variant="primary" onClick={f.save} disabled={f.saving || !f.orgId}>
                {f.saving ? (f.picked.invite ? 'Creating…' : 'Adding…') : (f.picked.invite ? 'Create account' : 'Add user')}
            </Button>
        </>
    ) : <Button onClick={onClose}>Cancel</Button>;

    return (
        <Dialog open onClose={onClose} title="Add user from Entra" initialFocusRef={searchRef} footer={footer}>
            {!f.picked ? (
                <>
                    <p className="ad2-hint">Search your directory for someone who already has a sign-in — or type an email to create a new account.</p>
                    <label className="ad2-search ad2-search--full">
                        <span className="v2-sr">Search directory</span>
                        <SearchIcon />
                        <input
                            ref={searchRef}
                            type="search"
                            placeholder="Search name or email…"
                            autoComplete="off"
                            value={f.query}
                            onChange={(e) => f.setQuery(e.target.value)}
                        />
                    </label>
                    <div className="ad2-dir" aria-live="polite">
                        {f.query.trim().length < 2 && <p className="ad2-muted ad2-small">Type at least 2 characters.</p>}
                        {f.searching && <p className="ad2-muted ad2-small">Searching Entra…</p>}
                        {!f.searching && f.searchError && <div className="ad2-alert" role="alert">{f.searchError}</div>}
                        {f.noMatches && <p className="ad2-muted ad2-small">No matches in the directory.</p>}
                        {f.results.map((r) => (
                            <button key={r.entra_object_id} type="button" className="ad2-dir-hit" onClick={() => f.setPicked(r)}>
                                <span className="ad2-tx"><b>{r.display_name}</b><span>{r.email}</span></span>
                                <span className="ad2-tag2">Has a sign-in</span>
                            </button>
                        ))}
                        {f.canCreate && (
                            <button type="button" className="ad2-dir-hit" onClick={f.pickNew}>
                                <span className="ad2-ua ad2-ua--dealer" aria-hidden="true"><PlusIcon /></span>
                                <span className="ad2-tx"><b>Create {f.query.trim()} as a new account</b><span>New — creates a local account with a temp password</span></span>
                            </button>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="ad2-picked">
                        <span className="ad2-tx">
                            <b>{f.picked.invite ? f.picked.email : f.picked.display_name}</b>
                            <span>{f.picked.invite ? 'New — creates a local account with a temp password' : f.picked.email}</span>
                        </span>
                        <Button variant="link" onClick={() => f.setPicked(null)}>Change</Button>
                    </div>
                    <div className="ad2-two">
                        <label className="ad2-fld">
                            <span>Role</span>
                            <select ref={roleRef} className="v2-sel" aria-label="Role" value={f.role} onChange={(e) => f.setRole(e.target.value)}>
                                {f.roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                            {!isSuper && <span className="ad2-hint">Admin roles are granted by Fusable.</span>}
                        </label>
                        <label className="ad2-fld">
                            <span>Organization</span>
                            <select className="v2-sel" value={f.orgId} onChange={(e) => f.setOrgId(e.target.value)} disabled={f.role === 'super_admin'}>
                                {f.flatOrgs.map((o) => <option key={o.id} value={o.id}>{indent(o.depth)}{o.name}</option>)}
                            </select>
                        </label>
                    </div>
                    {f.pickedOrg && (
                        <p className="ad2-hint">This decides what they see: <strong>{f.perspective}</strong>.</p>
                    )}
                    <label className="ad2-chk">
                        <input type="checkbox" checked={f.pricepath} onChange={(e) => f.setPricepath(e.target.checked)} />
                        <span>Grant IronSight access</span>
                    </label>
                    {preview && (
                        <div className={`ad2-verdict ad2-verdict--${preview.level}`}>
                            <span className="v2-eyebrow">They will have (expected)</span>
                            <AccessChip summary={preview} />
                        </div>
                    )}
                    {f.pickedOrg?.type === 'TOG' && (
                        <Note tone="warn">Holding members see no dealership data today. Pick a dealership instead.</Note>
                    )}
                    {f.error && <div className="ad2-alert" role="alert">{f.error}</div>}
                </>
            )}
        </Dialog>
    );
}
AddUserDialog.propTypes = {
    orgs: PropTypes.array.isRequired, me: PropTypes.object, tenants: PropTypes.array, onClose: PropTypes.func.isRequired, onAdded: PropTypes.func.isRequired,
};

// ── Sent / not sent (P-ADM-081..083) ───────────────────────────────────
export function ResultDialog({ result, onClose, onOpenProfile }) {
    const copy = resultCopy(Boolean(result.email_sent));
    const doneRef = useRef(null);
    return (
        <Dialog
            open
            onClose={onClose}
            title={copy.title}
            role={result.email_sent ? 'dialog' : 'alertdialog'}
            initialFocusRef={doneRef}
            footer={(
                <>
                    {onOpenProfile && <Button onClick={onOpenProfile}>Open profile</Button>}
                    <Button ref={doneRef} variant="primary" onClick={onClose}>Done</Button>
                </>
            )}
        >
            <div className="ad2-result">
                <span className={`ad2-result-ic ad2-result-ic--${result.email_sent ? 'ok' : 'bad'}`}><MailIcon /></span>
                <p>{copy.before}<strong>{result.email}</strong>{copy.after}</p>
            </div>
        </Dialog>
    );
}
ResultDialog.propTypes = { result: PropTypes.object.isRequired, onClose: PropTypes.func.isRequired, onOpenProfile: PropTypes.func };

// ── Edit org (P-ADM-102..107) ──────────────────────────────────────────
export function OrgEditDialog({ org, orgs, isSuper, onClose, onSaved }) {
    const f = useOrgEditForm({ org, orgs, isSuper, onSaved, onClose });
    const nameRef = useRef(null);
    const type = EDIT_TYPE_LABEL[org.type] || 'organization';
    return (
        <Dialog
            open
            onClose={onClose}
            title={`Edit ${type.toLowerCase()}`}
            initialFocusRef={nameRef}
            footer={(
                <>
                    <Button onClick={onClose} disabled={f.saving}>Cancel</Button>
                    <Button variant="primary" onClick={f.save} disabled={f.saving || !f.name.trim()}>{f.saving ? 'Saving…' : 'Save changes'}</Button>
                </>
            )}
        >
            <label className="ad2-fld">
                <span>Name</span>
                <input ref={nameRef} className="v2-input" value={f.name} onChange={(e) => f.setName(e.target.value)} />
            </label>
            <div className="ad2-fld">
                <span>Sign-in address</span>
                <PrefixInput prefix="/login/" label="Sign-in address" value={f.slug} onChange={f.setSlug} />
                {f.slugChanged && <span className="ad2-hint ad2-hint--warn">{COPY.slugWarning(type.toLowerCase())}</span>}
            </div>
            {f.canSuspend && (
                <div className="ad2-fld">
                    <span>Status</span>
                    <div className="v2-seg ad2-seg-start" role="group" aria-label="Status">
                        <button type="button" aria-pressed={f.status === 'active'} onClick={() => f.setStatus('active')}>Active</button>
                        <button type="button" aria-pressed={f.status === 'suspended'} onClick={() => f.setStatus('suspended')}>Suspended</button>
                    </div>
                    {f.status === 'suspended' && org.status !== 'suspended' && (
                        <span className="ad2-hint ad2-hint--warn">{COPY.suspendWarning}</span>
                    )}
                </div>
            )}
            {f.canMove && (
                <label className="ad2-fld">
                    <span>Move to</span>
                    <select className="v2-sel" value={f.parentId} onChange={(e) => f.setParentId(e.target.value)}>
                        <option value="__keep__">Keep where it is</option>
                        {f.moveOptions.map((o) => <option key={o.id || 'root'} value={o.id}>{o.label}</option>)}
                    </select>
                </label>
            )}
            {f.error && <div className="ad2-alert" role="alert">{f.error}</div>}
        </Dialog>
    );
}
OrgEditDialog.propTypes = {
    org: PropTypes.object.isRequired, orgs: PropTypes.array.isRequired, isSuper: PropTypes.bool, onClose: PropTypes.func.isRequired, onSaved: PropTypes.func.isRequired,
};

// ── Add dealership under a holding (P-ADM-108) ─────────────────────────
export function AddDealershipDialog({ c, onAdded }) {
    const nameRef = useRef(null);
    const close = () => c.setAddChild(null);
    const submit = () => {
        const name = c.addChildName.trim();
        const parent = c.addChild.parentName;
        c.doAddChild().then((ok) => { if (ok) onAdded?.(`${name} added under ${parent}`); });
    };
    return (
        <Dialog
            open
            onClose={close}
            title="Add dealership"
            initialFocusRef={nameRef}
            footer={(
                <>
                    <Button onClick={close} disabled={c.orgBusy}>Cancel</Button>
                    <Button variant="primary" onClick={submit} disabled={c.orgBusy || !c.addChildName.trim()}>{c.orgBusy ? 'Adding…' : 'Add'}</Button>
                </>
            )}
        >
            <p className="ad2-hint">Adds under <strong>{c.addChild.parentName}</strong>.</p>
            <label className="ad2-fld">
                <span>Dealership name</span>
                <input
                    ref={nameRef}
                    className="v2-input"
                    value={c.addChildName}
                    placeholder="e.g. Northern Equipment"
                    onChange={(e) => c.setAddChildName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                />
            </label>
            {c.orgError && <div className="ad2-alert" role="alert">{c.orgError}</div>}
        </Dialog>
    );
}
AddDealershipDialog.propTypes = { c: PropTypes.object.isRequired, onAdded: PropTypes.func };

// ── Delete org (P-ADM-109, decision 2) ─────────────────────────────────
export function DeleteOrgDialog({ c, users, tenants, onDeleted }) {
    const org = findNode(c.orgs, c.deleteTarget.id) || c.deleteTarget;
    const blockers = deleteBlockers(org, users, tenants);
    const pre = deleteBlockerText(blockers);
    const cancelRef = useRef(null);
    const guard = useConfirmGuard(true);
    const close = () => c.setDeleteTarget(null);
    const confirm = () => {
        const { name } = org;
        c.doDelete().then((ok) => { if (ok) onDeleted?.(`${name} deleted`); });
    };
    return (
        <Dialog
            open
            onClose={close}
            role="alertdialog"
            title={`Delete “${org.name}”?`}
            initialFocusRef={cancelRef}
            footer={(
                <>
                    <Button ref={cancelRef} onClick={close} disabled={c.orgBusy}>Cancel</Button>
                    <Button variant="danger" onClick={guard(confirm)} disabled={c.orgBusy || blockers.blocked}>{c.orgBusy ? 'Deleting…' : 'Delete'}</Button>
                </>
            )}
        >
            <p className="v2-md-msg">{COPY.deleteBody((TYPE_LABEL[org.type] || 'organization').toLowerCase())}</p>
            {pre && <Note tone="bad"><b>Not empty.</b> {pre}</Note>}
            {c.orgError && <div className="ad2-alert" role="alert">{c.orgError}</div>}
        </Dialog>
    );
}
DeleteOrgDialog.propTypes = {
    c: PropTypes.object.isRequired, users: PropTypes.array, tenants: PropTypes.array, onDeleted: PropTypes.func,
};

// ── Onboard a dealership (P-ADM-110..120) ──────────────────────────────
export function OnboardDialog({ orgs, onClose, onDone }) {
    const w = useOnboardWizard({ orgs, onClose, onDone });
    const nameRef = useRef(null);
    const { step, created, tenant } = w;

    let footer;
    if (created) {
        footer = (
            <>
                <span className="ad2-spacer" />
                <Button variant="primary" onClick={w.close} disabled={!!w.busy}>Done</Button>
            </>
        );
    } else {
        footer = (
            <>
                <Button onClick={step === 0 ? w.close : () => w.setStep((s) => s - 1)} disabled={w.saving}>{step === 0 ? 'Cancel' : 'Back'}</Button>
                <span className="ad2-spacer" />
                {step < ONBOARD_STEPS.length - 1 ? (
                    <Button variant="primary" onClick={() => w.setStep((s) => s + 1)} disabled={!w.canNext}>Next</Button>
                ) : (
                    <>
                        <Button onClick={() => w.submit(false)} disabled={w.saving}>Skip — connect the source later</Button>
                        <Button variant="primary" onClick={() => w.submit(true)} disabled={w.saving || !w.hasEndpoint}>
                            {w.saving ? 'Creating…' : 'Create & connect source'}
                        </Button>
                    </>
                )}
            </>
        );
    }

    return (
        <Dialog open wide onClose={w.close} title="Onboard a dealership" initialFocusRef={nameRef} footer={footer}>
            <ol className="ad2-stepper" aria-label="Steps">
                {ONBOARD_STEPS.map((s, i) => (
                    <li key={s} className={i === step ? 'is-on' : i < step ? 'is-done' : ''} aria-current={i === step ? 'step' : undefined}>
                        <b aria-hidden="true">{i < step ? '✓' : i + 1}</b>{s}
                    </li>
                ))}
            </ol>
            <div className="ad2-progress" aria-hidden="true"><i style={{ width: `${((step + 1) / ONBOARD_STEPS.length) * 100}%` }} /></div>

            {step === 0 && (
                <>
                    <label className="ad2-fld">
                        <span>Dealership name</span>
                        <input ref={nameRef} className="v2-input" type="text" placeholder="e.g. Midwest Equipment Co." value={w.dealerName} onChange={(e) => w.onName(e.target.value)} />
                    </label>
                    <div className="ad2-fld">
                        <span>Sign-in address</span>
                        <PrefixInput prefix="/login/" label="Sign-in address" value={w.effSlug} onChange={w.onSlug} />
                        <span className="ad2-hint">The dealer’s people sign in at this workspace address.</span>
                    </div>
                </>
            )}

            {step === 1 && (
                <>
                    <div className="ad2-fld">
                        <span>Is this dealership part of a holding group?</span>
                        <div className="ad2-choice">
                            <button type="button" aria-pressed={!w.inHolding} onClick={() => w.setInHolding(false)}>No — standalone</button>
                            <button type="button" aria-pressed={w.inHolding} onClick={() => w.setInHolding(true)}>Yes, it’s part of a group</button>
                        </div>
                    </div>
                    {w.inHolding && (
                        <label className="ad2-fld">
                            <span>Holding group</span>
                            <select className="v2-sel" value={w.holdingChoice} onChange={(e) => w.setHoldingChoice(e.target.value)}>
                                <option value="">Select a holding…</option>
                                {w.holdings.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                                <option value="__new__">+ Create a new holding…</option>
                            </select>
                            {w.holdingChoice === '__new__' && (
                                <input className="v2-input" type="text" aria-label="Holding name" placeholder="Holding name (e.g. Brand Holdings)" value={w.holdingName} onChange={(e) => w.setHoldingName(e.target.value)} />
                            )}
                        </label>
                    )}
                </>
            )}

            {step === 2 && (
                <>
                    <p className="ad2-hint">Invite the person who’ll run this dealership — they become its admin and can add their own team. You can skip and do it later.</p>
                    <div className="ad2-two">
                        <label className="ad2-fld">
                            <span>Admin email</span>
                            <input className="v2-input" type="email" placeholder="name@dealer.com" value={w.adminEmail} onChange={(e) => w.setAdminEmail(e.target.value)} />
                        </label>
                        <label className="ad2-fld">
                            <span>Name (optional)</span>
                            <input className="v2-input" type="text" placeholder="Full name" value={w.adminName} onChange={(e) => w.setAdminName(e.target.value)} />
                        </label>
                    </div>
                    {w.adminEmail.trim() && <Note>{COPY.onboardAdminHint}</Note>}
                </>
            )}

            {step === 3 && !created && (
                <>
                    <p className="ad2-hint">
                        Where should IronSight read this dealership’s data from? The data team builds and
                        registers the warehouse — paste the endpoint they gave you and we’ll connect to it.
                    </p>
                    <SourceFields value={w.source} onChange={w.setSource} disabled={w.saving} idPrefix="ob" />
                </>
            )}

            {step === 3 && created && (
                <div className="ad2-ob-done">
                    <Note tone="ok">
                        <strong>{created.dealer?.name || w.dealerName}</strong> created
                        {created.admin ? <> · <strong>{created.admin.email}</strong> invited as its admin</> : null}.
                    </Note>
                    {tenant ? (
                        <>
                            <div className="ad2-picked">
                                <span className="ad2-tx">
                                    <b className="ad2-ep">{tenant.synapse_server}</b>
                                    <span>
                                        {tenant.database || DEFAULT_DATABASE}
                                        {tenant.lake_account ? ` · ${tenant.lake_account}` : ' · no storage account'}
                                    </span>
                                </span>
                                <SourceChip tenant={tenant} />
                            </div>
                            <div className="ad2-row">
                                <Button onClick={w.runCheck} disabled={!!w.busy}>{w.busy === 'check' ? 'Testing…' : 'Test connection'}</Button>
                                <Button
                                    onClick={w.activate}
                                    disabled={!!w.busy || w.blocked || tenant.status === 'active'}
                                    title={w.blocked ? 'Resolve the failing checks first.' : undefined}
                                >
                                    {w.busy === 'activate' ? 'Activating…' : tenant.status === 'active' ? 'Active' : 'Activate'}
                                </Button>
                            </div>
                            {w.busy === 'check' || w.busy === 'activate' ? <CheckPending /> : <CheckResult result={w.check} />}
                        </>
                    ) : (
                        <>
                            <p className="ad2-hint">
                                No data source is connected yet. You can attach one any time from
                                <strong> Data sources</strong> — the dealership just stays empty until then.
                            </p>
                            {w.hasEndpoint && (
                                <div className="ad2-row">
                                    <Button disabled={w.busy === 'connect'} onClick={() => w.connect(created.dealer?.id, created.dealer?.slug)}>
                                        {w.busy === 'connect' ? 'Connecting…' : 'Retry connecting the source'}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {w.error && <div className="ad2-alert" role="alert">{w.error}</div>}
        </Dialog>
    );
}
OnboardDialog.propTypes = { orgs: PropTypes.array.isRequired, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired };

// ── Connect / re-point a data source (P-ADM-130) ───────────────────────
export function AttachSourceDialog({ dealers, tenants, dealerId, onClose, onSaved }) {
    const f = useAttachSource({ dealers, tenants, dealerId, onSaved, onClose });
    const firstRef = useRef(null);
    return (
        <Dialog
            open
            onClose={onClose}
            title={f.existing ? 'Re-point data source' : 'Connect a data source'}
            initialFocusRef={firstRef}
            footer={(
                <>
                    <Button onClick={onClose} disabled={f.saving}>Cancel</Button>
                    <Button variant="primary" onClick={f.save} disabled={f.saving || !f.canSave}>
                        {f.saving ? 'Connecting…' : f.existing ? 'Save source' : 'Connect source'}
                    </Button>
                </>
            )}
        >
            <div className="ad2-two">
                <label className="ad2-fld">
                    <span>Dealership</span>
                    <select ref={firstRef} className="v2-sel" value={f.orgId} disabled={f.saving} onChange={(e) => f.pickOrg(e.target.value)}>
                        {dealers.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}{f.tenantFor(d.id) ? ' — already connected' : ''}</option>
                        ))}
                    </select>
                </label>
                <div className="ad2-fld">
                    <span>Source key</span>
                    <PrefixInput prefix="tenant/" label="Source key" value={f.slug} onChange={f.setSlug} disabled={f.saving} />
                </div>
            </div>
            <SourceFields value={f.source} onChange={f.setSource} disabled={f.saving} idPrefix="at" />
            {f.existing && (
                <Note tone="warn">
                    This dealership already reads from a source. Saving a different endpoint re-points
                    it and puts it back to <strong>Waiting for data</strong> until it is verified again.
                </Note>
            )}
            {f.error && <div className="ad2-alert" role="alert">{f.error}</div>}
        </Dialog>
    );
}
AttachSourceDialog.propTypes = {
    dealers: PropTypes.array.isRequired, tenants: PropTypes.array.isRequired, dealerId: PropTypes.string, onClose: PropTypes.func.isRequired, onSaved: PropTypes.func.isRequired,
};
